import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from 'store';

import type { AccountFlowScreens } from 'common/accountActivationAndRecovery/AccountFlowScreens';
import { AccountScreen } from 'common/accountActivationAndRecovery/AccountScreen';
import { CIAnalytics } from 'common/analytics/analytics.provider';
import { usePageStatus } from 'common/hooks/use-page-status';

import { deviceHandoffScreenPath } from '../deviceHandoffScreenPaths';
import { selectRespondentTarget } from '../state/selectors';

export function useDeviceHandoffAccountFlow(): AccountFlowScreens {
    const { t } = useTranslation(['auth', 'user']);
    const navigate = useNavigate();
    const pageStatus = usePageStatus();
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);
    const respondentTarget = useSelector(selectRespondentTarget);

    const goTo = useCallback(
        (screen: AccountScreen) => navigate(deviceHandoffScreenPath(screen), { replace: true }),
        [navigate]
    );
    const goToLogin = useCallback(() => goTo(AccountScreen.Login), [goTo]);

    const onActivationComplete = useCallback((): void => {
        pageStatus.success(t('activation.patient_activation_success', { ns: 'user' }));
        goTo(AccountScreen.Login);
    }, [pageStatus, t, goTo]);

    const onPasswordCreated = useCallback((): void => {
        pageStatus.success(t('password_form.password_update_success'));
        CIAnalytics.getInstance().identifyUser(activeUser);
        goTo(AccountScreen.Login);
    }, [pageStatus, t, activeUser, goTo]);

    return useMemo<AccountFlowScreens>(
        () => ({
            activation: {
                targetUser: respondentTarget?.respondentPK
                    ? { type: 'userPk', value: respondentTarget.respondentPK }
                    : null,
                prefilledActivationCode: respondentTarget?.activationCode ?? undefined,
                activationCodeReadOnly: true,
                isPasswordReset: false,
                hideLogo: true,
                showStepper: true,
                showSuccessView: false,
                onComplete: onActivationComplete,
                onBackToLogin: goToLogin,
            },
            securityQuestion: {
                noQuestionsOnFileAction: 'goToActivation',
                onQuestionsFound: () => goTo(AccountScreen.SecurityAnswer),
                onNeedsActivation: () => goTo(AccountScreen.Activation),
                onForgotUsername: () => goTo(AccountScreen.ForgotUser),
                onBackToLogin: goToLogin,
                className: 'switch_user_security_question',
            },
            createPassword: {
                onCreated: onPasswordCreated,
                showSucceededView: false,
                className: 'switch_user_create_password',
            },
            securityAnswer: {
                onAnswerValidated: () => goTo(AccountScreen.CreatePassword),
                onQuestionsMissing: () => goTo(AccountScreen.SecurityQuestion),
                onNeedsActivation: () => goTo(AccountScreen.Activation),
                onBackToLogin: goToLogin,
                showLinks: true,
                showLockoutTimer: false,
                resetPasswordUserId: undefined,
                className: 'switch_user_security_answer',
            },
            forgotUser: {
                onChoosePassword: () => goTo(AccountScreen.SecurityQuestion),
                onBackToLogin: goToLogin,
                choiceClassName: 'switch_user_forgot_user_password',
                patientForgotUserIdClassName: 'switch_user',
            },
        }),
        [goTo, goToLogin, onActivationComplete, respondentTarget, onPasswordCreated]
    );
}
